import React from 'react';
import { FINBillData, LookupItem } from '../../../services/finBillDataApi';
import { dateToInput, inputToDate, fmt, STATUS_OPTIONS, BILL_TYPE_OPTIONS } from '../helpers';

interface Tab1Props {
  formData: Partial<FINBillData>;
  isEditing: boolean;
  isFieldDisabled: (key: string) => boolean;
  getField: (key: keyof FINBillData) => FINBillData[keyof FINBillData];
  setField: (key: keyof FINBillData, value: FINBillData[keyof FINBillData]) => void;
  getFieldError?: (field: string) => string | undefined;
  lookupBanks: LookupItem[];
  lookupCurrencies: LookupItem[];
  lookupCompanies: LookupItem[];
  onBillTypeChange: (val: string) => void;
}

const Tab1BasicInfo: React.FC<Tab1Props> = ({
  formData, isEditing, isFieldDisabled,
  getField, setField, getFieldError,
  lookupBanks, lookupCurrencies, lookupCompanies,
  onBillTypeChange,
}) => {
  const E = (field: string) => {
    const err = getFieldError?.(field);
    return err ? <span style={{ color: '#ff4d4f', fontSize: 11, marginLeft: 6 }}>{err}</span> : null;
  };

  return (
    <div className="tab1-layout">
      <div className="form-sections">

        {/* Section 1: 基本票據資訊 */}
        <div className="form-section odd">
          <span className="section-tag">基本資訊</span>
          <div className="form-grid">
            <label>單據號碼</label>
            <div>
              <input type="text" readOnly value={getField('bill_no') as string} style={{ maxWidth: 160 }} />
            </div>

            <label className="required">單據類別{E('bill_type')}</label>
            <div>
              <select
                disabled={isFieldDisabled('bill_type')}
                value={getField('bill_type') as string}
                onChange={e => onBillTypeChange(e.target.value)}
              >
                <option value="">--</option>
                {BILL_TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <label className="required">支票號碼{E('check_no')}</label>
            <div>
              <input
                type="text" style={{ maxWidth: 200 }}
                disabled={isFieldDisabled('check_no')}
                value={getField('check_no') as string}
                onChange={e => setField('check_no', e.target.value)}
              />
            </div>

            <label>票據類別</label>
            <div>
              <select
                disabled={isFieldDisabled('bill_sort')}
                value={getField('bill_sort') as string}
                onChange={e => setField('bill_sort', e.target.value)}
              >
                <option value="">--</option>
                <option value="P">P-支票</option>
                <option value="R">R-收票</option>
              </select>
            </div>

            <label className="required">票據狀態{E('status')}</label>
            <div>
              <select
                disabled={isFieldDisabled('status')}
                value={getField('status') as string}
                onChange={e => setField('status', e.target.value)}
              >
                {STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <label>公司代號</label>
            <div>
              <select
                disabled={isFieldDisabled('company')}
                value={getField('company') as string}
                onChange={e => setField('company', e.target.value)}
              >
                <option value="">--</option>
                {lookupCompanies.map(c => (
                  <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Section 2: 金額與幣別 */}
        <div className="form-section even">
          <span className="section-tag">金額幣別</span>
          <div className="form-grid">
            <label className="required">票面金額{E('check_amt')}</label>
            <div>
              <input
                type="number" step="0.01" style={{ maxWidth: 180 }}
                disabled={isFieldDisabled('check_amt')}
                value={getField('check_amt') as number}
                onChange={e => setField('check_amt', parseFloat(e.target.value) || 0)}
              />
              <span style={{ marginLeft: 8, color: '#666', fontSize: 12 }}>{fmt(formData.check_amt)}</span>
            </div>

            <label>幣別</label>
            <div>
              <select
                disabled={isFieldDisabled('curr_code')}
                value={getField('curr_code') as string}
                onChange={e => setField('curr_code', e.target.value)}
              >
                <option value="">--</option>
                {lookupCurrencies.map(c => (
                  <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                ))}
              </select>
            </div>

            <label>匯率</label>
            <div>
              <input
                type="number" step="0.0001" style={{ maxWidth: 120 }}
                disabled={isFieldDisabled('rate')}
                value={getField('rate') as number}
                onChange={e => setField('rate', parseFloat(e.target.value) || 0)}
              />
            </div>
          </div>
        </div>

        {/* Section 3: 受款人與銀行 */}
        <div className="form-section odd">
          <span className="section-tag">受款人/銀行</span>
          <div className="form-grid">
            <label className="required">受款人{E('payee')}</label>
            <div style={{ gridColumn: 'span 3' }}>
              <input
                type="text"
                disabled={isFieldDisabled('payee')}
                value={getField('payee') as string}
                onChange={e => setField('payee', e.target.value)}
              />
            </div>

            <label>出票銀行</label>
            <div>
              <select
                disabled={isFieldDisabled('check_bank')}
                value={getField('check_bank') as string}
                onChange={e => setField('check_bank', e.target.value)}
              >
                <option value="">--</option>
                {lookupBanks.map(b => (
                  <option key={b.code} value={b.code}>{b.code} - {b.name}</option>
                ))}
              </select>
            </div>

            <label>開票人</label>
            <div>
              <input
                type="text" style={{ maxWidth: 200 }}
                disabled={isFieldDisabled('make_check')}
                value={getField('make_check') as string}
                onChange={e => setField('make_check', e.target.value)}
              />
            </div>

            <label>付款銀行</label>
            <div>
              <select
                disabled={isFieldDisabled('pay_bank')}
                value={getField('pay_bank') as string}
                onChange={e => setField('pay_bank', e.target.value)}
              >
                <option value="">--</option>
                {lookupBanks.map(b => (
                  <option key={b.code} value={b.code}>{b.code} - {b.name}</option>
                ))}
              </select>
            </div>

            <label className="required">付款銀行帳號{E('pay_bank_acc_no')}</label>
            <div>
              <input
                type="text" style={{ maxWidth: 220 }}
                disabled={isFieldDisabled('pay_bank_acc_no')}
                value={getField('pay_bank_acc_no') as string}
                onChange={e => setField('pay_bank_acc_no', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Section 4: 日期 */}
        <div className="form-section even">
          <span className="section-tag">日期資訊</span>
          <div className="form-grid">
            <label>開立日期</label>
            <div>
              <input
                type="date"
                disabled={isFieldDisabled('make_date')}
                value={dateToInput(getField('make_date') as string)}
                onChange={e => setField('make_date', inputToDate(e.target.value) as string)}
              />
            </div>

            <label className="required">到期日期{E('expire_date')}</label>
            <div>
              <input
                type="date"
                disabled={isFieldDisabled('expire_date')}
                value={dateToInput(getField('expire_date') as string)}
                onChange={e => setField('expire_date', inputToDate(e.target.value) as string)}
              />
            </div>

            <label>收票日期</label>
            <div>
              <input
                type="date"
                disabled={isFieldDisabled('rec_date')}
                value={dateToInput(getField('rec_date') as string)}
                onChange={e => setField('rec_date', inputToDate(e.target.value) as string)}
              />
            </div>

            <label>出票日期</label>
            <div>
              <input
                type="date"
                disabled={isFieldDisabled('out_date')}
                value={dateToInput(getField('out_date') as string)}
                onChange={e => setField('out_date', inputToDate(e.target.value) as string)}
              />
            </div>

            <label>入帳日期</label>
            <div>
              <input
                type="date"
                disabled={isFieldDisabled('in_date')}
                value={dateToInput(getField('in_date') as string)}
                onChange={e => setField('in_date', inputToDate(e.target.value) as string)}
              />
            </div>

            <label>兌現日期</label>
            <div>
              <input
                type="date"
                disabled={isFieldDisabled('cash_date')}
                value={dateToInput(getField('cash_date') as string)}
                onChange={e => setField('cash_date', inputToDate(e.target.value) as string)}
              />
            </div>

            <label>作廢日期</label>
            <div>
              <input
                type="date"
                disabled={isFieldDisabled('cancel_date')}
                value={dateToInput(getField('cancel_date') as string)}
                onChange={e => setField('cancel_date', inputToDate(e.target.value) as string)}
              />
            </div>
          </div>
        </div>

        {/* Section 5: 托收/兌現 */}
        <div className="form-section odd">
          <span className="section-tag">托收/兌現</span>
          <div className="form-grid">
            <label>托收銀行</label>
            <div>
              <select
                disabled={isFieldDisabled('rec_bank')}
                value={getField('rec_bank') as string}
                onChange={e => setField('rec_bank', e.target.value)}
              >
                <option value="">--</option>
                {lookupBanks.map(b => (
                  <option key={b.code} value={b.code}>{b.code} - {b.name}</option>
                ))}
              </select>
            </div>

            <label>托收銀行帳號</label>
            <div>
              <input
                type="text" style={{ maxWidth: 220 }}
                disabled={isFieldDisabled('rec_bank_acc_no')}
                value={getField('rec_bank_acc_no') as string}
                onChange={e => setField('rec_bank_acc_no', e.target.value)}
              />
            </div>

            <label>兌現銀行</label>
            <div>
              <select
                disabled={isFieldDisabled('cash_bank')}
                value={getField('cash_bank') as string}
                onChange={e => setField('cash_bank', e.target.value)}
              >
                <option value="">--</option>
                {lookupBanks.map(b => (
                  <option key={b.code} value={b.code}>{b.code} - {b.name}</option>
                ))}
              </select>
            </div>

            <label>兌現銀行帳號</label>
            <div>
              <input
                type="text" style={{ maxWidth: 220 }}
                disabled={isFieldDisabled('cash_bank_acc_no')}
                value={getField('cash_bank_acc_no') as string}
                onChange={e => setField('cash_bank_acc_no', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Section 6: 摘要與其他 */}
        <div className="form-section even">
          <span className="section-tag">其他</span>
          <div className="form-grid">
            <label>摘要</label>
            <div style={{ gridColumn: 'span 5' }}>
              <textarea
                rows={3}
                disabled={isFieldDisabled('summary')}
                value={getField('summary') as string}
                onChange={e => setField('summary', e.target.value)}
              />
            </div>

            <label>記帳否</label>
            <div>
              <input
                type="checkbox"
                disabled={isFieldDisabled('no_jb')}
                checked={!!formData.no_jb}
                onChange={e => setField('no_jb', e.target.checked)}
              />
            </div>

            <label>不含在途</label>
            <div>
              <input
                type="checkbox"
                disabled={isFieldDisabled('no_tt')}
                checked={!!formData.no_tt}
                onChange={e => setField('no_tt', e.target.checked)}
              />
            </div>

            <label>是否遺失</label>
            <div>
              <input
                type="checkbox"
                disabled={isFieldDisabled('is_mis')}
                checked={!!formData.is_mis}
                onChange={e => setField('is_mis', e.target.checked)}
              />
            </div>

            <label>建立人</label>
            <div>
              <input type="text" readOnly value={getField('c_user') as string} style={{ maxWidth: 120 }} />
            </div>

            <label>建立日期</label>
            <div>
              <input type="text" readOnly value={getField('c_date') as string} style={{ maxWidth: 120 }} />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default React.memo(Tab1BasicInfo);
